import React, { useState } from 'react';
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Checkbox, Form, Input } from 'antd';
import LoadableButton from "../../../../../components/buttons/LoadableButton.jsx";
import { handleApiError } from '../../../../../utils/handleApiError.js';
import OTPModal from "../../../../../components/modals/OTPModal.jsx";
import { AddWithdrawRequestApi, ResendWithdrawTransactionApi, VerifyWithdrawTransactionApi } from '../../../../../api/request/wallet/withdraw.js';

const BankTransfer = ({ amount, resetAll, validateAmount, paymentGatewayId }) => {
  const [form] = Form.useForm();
  const { userDetails } = useSelector(state => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      if (validateAmount) {
        toast.error(validateAmount);
        return;
      }
      if (!amount) {
        toast.error('Please enter withdraw amount');
        return;
      }
      const params = {
        userId: userDetails?._id,
        PaymentGatwayId: paymentGatewayId,
        amount: Number(amount),
        withdrawalMethod: 1,
        ...values
      };
      const { data } = await AddWithdrawRequestApi(params);
      if (data?.success) {
        toast.success(data?.message);
        setRequestId(data?.data?.requestId);
        setIsOTPModalOpen(true);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }
  const handleOtpSubmit = async (otpVal) => {
    try {
      setOtpLoading(true);
      const params = {
        requestId: requestId,
        otp: otpVal,
        actionType: 1,
      };
      const { data } = await VerifyWithdrawTransactionApi(params);
      if (data?.success) {
        toast.success(data?.message);
        setIsOTPModalOpen(false);
        setOtp("");
        form?.resetFields();
        resetAll();
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpResend = async () => {
    try {
      setOtp("");
      if (!requestId) {
        toast.error('Please request for withdraw first');
        return false;
      }
      const params = {
        requestId: requestId,
        actionType: 1,
      };
      const { data } = await ResendWithdrawTransactionApi(params);
      if (data?.success) {
        toast.success('OTP resent');
        return true;
      }
    } catch (err) {
      handleApiError(err);
    }
    return false;
  };

  return (
    <div>
      <Form form={form} layout="vertical" className='space-y-5' onFinish={handleSubmit}>
        <div className="grid sm:grid-cols-2 gap-5">
          <Form.Item
            name="bankName"
            label={<span className="text-lg text-gray-500 font-medium">Bank Name</span>}
            rules={[{ required: true, message: 'Please enter bank name' }]}
          >
            <Input placeholder="Bank name" size='large' className='py-3 md:py-1.5' />
          </Form.Item>

          <Form.Item
            name="fullName"
            label={<span className="text-lg text-gray-500 font-medium">Account Holder Name</span>}
            rules={[{ required: true, message: 'Please enter account holder name' }]}
          >
            <Input placeholder="Account holder name" size='large' className='py-3 md:py-1.5' />
          </Form.Item>

          <Form.Item
            name="bankAccountNumber"
            label={<span className="text-lg text-gray-500 font-medium">Account Number</span>}
            rules={[{ required: true, message: 'Please enter account number' }]}
          >
            <Input type='number' placeholder="Account number" size='large' className='py-3 md:py-1.5' />
          </Form.Item>

          <Form.Item
            name="iban"
            label={<span className="text-lg text-gray-500 font-medium">IBAN Code</span>}
            rules={[{ required: true, message: 'Please enter iban code' }]}
          >
            <Input placeholder="IBAN code" size='large' className='py-3 md:py-1.5' />
          </Form.Item>

          <Form.Item
            name="swifT_BIC_Code"
            label={<span className="text-lg text-gray-500 font-medium">Swift Code</span>}
            rules={[{ required: true, message: 'Please enter Swift Code' }]}
          >
            <Input placeholder="Swift code" size='large' className='py-3 md:py-1.5' />
          </Form.Item>

          <Form.Item
            name="bankBranchAddress"
            label={<span className="text-lg text-gray-500 font-medium">Bank Branch Address</span>}
            rules={[{ required: true, message: 'Please enter bank branch address' }]}
          >
            <Input placeholder="Bank branch address" size='large' className='py-3 md:py-1.5' />
          </Form.Item>
        </div>

        <Form.Item
          name='conditions'
          valuePropName="checked"
          rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
        >
          <Checkbox className='mt-4'>
            Yes, I agreed to the <Link to={'/terms_&_conditions'} className='primary hover:underline duration-300'>Terms and conditions</Link>
          </Checkbox>
        </Form.Item>

        <LoadableButton
          className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-3 md:py-2 w-full md:w-auto uppercase themeHover duration-500'
          type="submit"
          lable='Submit'
          isLoading={isLoading}
          loadingLable='Adding request...'
        />
      </Form>
      <OTPModal
        isOpen={isOTPModalOpen}
        setIsOpen={setIsOTPModalOpen}
        title='OTP Verification'
        message='Please enter the OTP to verify withdrawal request.'
        width={600}
        value={otp}
        onChange={(val) => setOtp(val)}
        onSubmit={handleOtpSubmit}
        onResend={handleOtpResend}
        isLoading={otpLoading}
        loadingLabel='Verifying...'
        submitLabel='Verify'
        otpDuration={120}
      />
    </div>
  )
}

export default BankTransfer


